/**
 * LogSummarySection — renders the structured Datadog log search output.
 *
 * Each service is a sub-section with its top error kinds. Clicking an error
 * kind expands its full-text sample messages (no truncation). This is the
 * UI side of Phase A's structured shaper.
 */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { LogSummaryContext } from '@/types/analysis';

export interface LogSummarySectionProps {
  summary: LogSummaryContext;
}

export function LogSummarySection({ summary }: LogSummarySectionProps) {
  if (summary.services.length === 0) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Log signal
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No errors in the search window for <code>{summary.filter_query}</code>.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Log signal
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {summary.total.toLocaleString()} matching log{summary.total !== 1 ? 's' : ''} ·{' '}
        <code>{summary.filter_query}</code>
      </Typography>

      <Stack spacing={2}>
        {summary.services.map((svc) => (
          <Box key={svc.name}>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                {svc.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {svc.total.toLocaleString()} log{svc.total !== 1 ? 's' : ''}
              </Typography>
            </Stack>

            <Stack spacing={1}>
              {svc.error_kinds.map((kind) => (
                <Accordion
                  key={`${svc.name}-${kind.kind}`}
                  disableGutters
                  elevation={0}
                  variant="outlined"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ minHeight: 44 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      <Chip
                        size="small"
                        label={kind.count.toLocaleString()}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {kind.kind}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack spacing={1.5}>
                      {kind.samples.map((sample, i) => (
                        <Box
                          key={`${kind.kind}-${i}`}
                          sx={{
                            borderLeft: '2px solid',
                            borderColor: 'divider',
                            pl: 1.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            {sample.timestamp}
                            {sample.host && ` · ${sample.host}`}
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              fontSize: 12,
                              lineHeight: 1.5,
                            }}
                          >
                            {sample.message}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
